/**
 * Learning Intelligence Signal Emission
 *
 * Frontend utility for emitting signals to the profile_signals table.
 * Used by Flashcards and other frontend features.
 *
 * Signals are processed by pg_cron or Edge Functions to update:
 * - profile_mastery_map (topic-level mastery)
 * - profile_beliefs (strengths/weaknesses)
 */

import { supabase } from './supabase';
import { normalizeSubject } from './subject-mapping';
import { LENGTH_LEGACY_SOURCE_TYPE } from './rehome/nextgen-length-legacy';

/**
 * `signal_data.origin` marking a signal as coming from a re-homed NextGen item.
 *
 * Re-exported from the ingest's own module rather than retyped, so the emitter,
 * the ingest (`scripts/rehome-nextgen-mcqs.ts`) and the partial unique index on
 * `(source_id) WHERE source_type = 'nextgen_length_legacy'` cannot drift.
 * The Python consumer holds the fourth copy —
 * `services/process-signals/signal_processor.py REHOMED_FLASHCARD_ORIGIN` —
 * which no compiler can check, so a test on each side asserts the literal.
 *
 * Drift is SILENT: the processor's `is_rehomed_flashcard()` simply returns
 * False forever, the legacy branch stamps `exam_format='mcq'`, and the
 * re-homed items quietly regain NextGen format readiness.
 */
export const REHOMED_FLASHCARD_ORIGIN = LENGTH_LEGACY_SOURCE_TYPE;

/**
 * The learning-intelligence axes a re-homed NextGen item carries.
 *
 * Written into `flashcards.metadata` by
 * `src/lib/rehome/nextgen-length-legacy.ts::planCard`, copied verbatim from the
 * source `nextgen_questions` row.
 */
export interface RehomedCardAxes {
  origin: string;
  source_question_id: string;
  subject?: string;
  topic?: string;
  skill_domain?: string;
  ncbe_tasks?: number[];
}

/**
 * Extract the re-home axes from a flashcard row, or `undefined` for any card
 * that is not a re-homed NextGen item.
 *
 * WHY THIS IS A HELPER AND NOT FOUR PARAMETERS PER CALL SITE
 * ----------------------------------------------------------
 * There are 11 flashcard-signal construction sites across three files
 * (use-query-flashcards.ts, UnifiedStudyMode.tsx, FlashcardStudy.tsx). Threading
 * four optional fields through all of them by hand is precisely the shape that
 * fails silently: `signal_data` is a loose object, so omitting `skill_domain` at
 * one site is not a type error — it just reverts that surface to the crude
 * `legal_research` default, and the belief's skill then flips depending on which
 * SURFACE the student last used (the upsert does
 * `skill_domain = COALESCE(EXCLUDED.skill_domain, …)`).
 *
 * One expression per call site — `rehomed: rehomedAxesFromCard(card)` — makes
 * every surface agree by construction.
 *
 * Reads `metadata` ONLY, never `source_metadata`. That is deliberate:
 * `source_metadata.exam_format` holds the item's ORIGINAL NextGen format as an
 * audit breadcrumb, and reading it here would re-introduce the exact format tag
 * this design exists to withhold.
 */
export function rehomedAxesFromCard(
  card: { source_type?: string | null; metadata?: unknown } | null | undefined
): RehomedCardAxes | undefined {
  if (!card || card.source_type !== REHOMED_FLASHCARD_ORIGIN) return undefined;
  const meta = (card.metadata ?? {}) as Record<string, unknown>;
  const sourceQuestionId = meta.source_question_id;
  if (typeof sourceQuestionId !== 'string' || !sourceQuestionId) return undefined;

  const skillDomain = typeof meta.skill_domain === 'string' && meta.skill_domain
    ? meta.skill_domain
    : undefined;
  const subject = typeof meta.subject === 'string' && meta.subject ? meta.subject : undefined;
  const topic = typeof meta.topic === 'string' && meta.topic ? meta.topic : undefined;
  const ncbeTasks = Array.isArray(meta.ncbe_tasks)
    ? meta.ncbe_tasks.filter((n): n is number => typeof n === 'number' && Number.isFinite(n))
    : undefined;

  return {
    origin: REHOMED_FLASHCARD_ORIGIN,
    source_question_id: sourceQuestionId,
    ...(subject ? { subject } : {}),
    ...(topic ? { topic } : {}),
    ...(skillDomain ? { skill_domain: skillDomain } : {}),
    ...(ncbeTasks && ncbeTasks.length ? { ncbe_tasks: ncbeTasks } : {}),
  };
}

/**
 * The Learning-Intelligence axis tags carried on a flashcard signal:
 * `exam_format` and `skill_domain`. Flashcard signals carry NO `product` key;
 * process-signals reads an absent product as 'nextgen'.
 *
 * There is no `product` option any more. The only non-default value it ever
 * took was 'ube' (Legacy Bar Prep's MBE axis: product='ube', exam_format='mbe'),
 * and that product was canceled 2026-10-10. process-signals now IGNORES
 * product='ube' on every product axis, so re-adding it here would emit
 * practice that is silently dropped. Pinned by
 * services/process-signals/tests/test_no_ube_product_emitter.py.
 *
 * Resolves the format/skill axis for a recall-type flashcard signal so all
 * builders stay consistent.
 *
 * Behavior contract:
 *   - issue_spotting cards → IQS axis ('iqs' / 'issue_spotting_analysis').
 *   - everything else → the legacy default: exam_format='mcq',
 *     skill_domain='legal_research'.
 */
function resolveFlashcardSignalAxes(opts: {
  cardType?: string;
  rehomed?: RehomedCardAxes;
}): {
  exam_format?: string;
  skill_domain?: string;
  origin?: string;
  source_question_id?: string;
  ncbe_tasks?: number[];
  subject?: string;
  topic?: string;
} {
  // Re-homed NextGen items — MUST be the first branch.
  //
  // These are real NextGen MCQs living in the flashcards product, so they
  // carry the REAL subject / topic / skill_domain / ncbe_tasks from the source
  // question instead of the crude `legal_research` default. Subject and skill
  // KNOWLEDGE is accurate; NextGen READINESS stays unreachable
  // (.claude/rules/free-tier-value-claims.md).
  //
  // NO `exam_format` KEY IS RETURNED, and that omission is load-bearing rather
  // than cosmetic. `profile_beliefs.exam_format` is the column the
  // format-readiness coverage query keys on. Since 2026-09-05 that query is
  // ALSO source-gated (signal_processor.py update_format_readiness,
  // `feature_sources && NEXTGEN_NATIVE_SOURCES`), so withholding the format is
  // the second, independent gate behind the source filter — kept by design.
  // A future edit that "helpfully" restores `exam_format: 'mcq'` here would
  // type-check, pass review, and hand NextGen readiness to free-tier users —
  // pinned by signals.rehome.test.ts.
  //
  // Ordering matters: this precedes the issue_spotting branch so a
  // re-homed card can never be re-routed by its card_type. The legacy return below is untouched, so every non-re-homed card
  // emits byte-identically to before.
  if (opts.rehomed) {
    const { origin, source_question_id, subject, topic, skill_domain, ncbe_tasks } = opts.rehomed;
    return {
      origin,
      source_question_id,
      ...(subject ? { subject } : {}),
      ...(topic ? { topic } : {}),
      ...(skill_domain ? { skill_domain } : {}),
      ...(ncbe_tasks ? { ncbe_tasks } : {}),
    };
  }
  // issue_spotting always maps to the IQS axis (product-independent).
  if (opts.cardType === 'issue_spotting') {
    return { exam_format: 'iqs', skill_domain: 'issue_spotting_analysis' };
  }
  // Legacy / NextGen default — byte-identical to pre-C17.
  return { exam_format: 'mcq', skill_domain: 'legal_research' };
}

// Signal types and weights from the taxonomy
export const SIGNAL_WEIGHTS = {
  // Flashcards (moderate fidelity - recall not application)
  'flashcards.card_answered': 0.6,
  'flashcards.card_mastered': 0.7,
  'flashcards.card_pinned': 0.8,  // User-declared weakness - high signal
  'flashcards.card_lapsed': 0.8,  // FSRS lapse: forgotten card (scheduled_days >= 7 rated wrong)
  'flashcards.session_completed': 0.6,

  // Issue Spotter signals
  'issue_spotter.issue_pinned': 0.8,  // User-declared weakness - high signal

  // Chat (low fidelity - noisy)
  'chat.topic_discussed': 0.3,
  'chat.confusion_expressed': 0.4,
  'chat.follow_up_needed': 0.35,

  // Study Plan / Activity signals (high fidelity - explicit completion)
  'study_plan.task_completed': 0.75,
  'study_plan.diagnostic_completed': 0.85,  // High signal - direct assessment
  'study_plan.activity_skipped': 0.5,

  // Course signals (low fidelity - passive consumption, not active practice)
  'course.lesson_completed': 0.15,  // Exposure signal; completed_at enables knowledge decay

  // Syllabus signals (high fidelity - user explicitly uploaded course info)
  'syllabus.tasks_generated': 0.7,  // User has explicit course schedule

  // Onboarding signals (cold start - user profile declarations)
  'onboarding.status_declared': 0.4,
  'onboarding.goal_declared': 0.4,
  'onboarding.profile_declared': 0.4,

  // NextGen Question Bank (high fidelity — direct assessment with FSRS tracking)
  'nextgen.question_answered': 0.85,
  'nextgen.question_mastered': 0.9,   // First time question crosses mastery threshold
  'nextgen.question_lapsed': 0.9,     // FSRS lapse: previously stable question answered wrong
  'nextgen.miss_reason_reported': 0.8,  // Self-reported miss reason — high signal, explicit user input
  'nextgen.tier_transition_seen': 0,    // Frontend-only marker — user saw celebration toast; zero weight, not a learning event

  // Workbench (moderate fidelity — task completion with optional accuracy)
  'workbench.task_completed': 0.70,
  'workbench.task_graded': 0.85,

  // Adherence signals (§18 — consumed by adherence_engine, weight 0 for beliefs)
  'adherence.session_completed': 0.7,
  'adherence.session_abandoned': 0.6,
  'adherence.recommendation_dismissed': 0.5,
  'adherence.login_without_practice': 0.2,

  // Cognitive load signals (§18 — telemetry for mid-session adaptation)
  'adherence.response_time_increasing': 0,
  'adherence.answer_changes': 0,
  'adherence.error_rate_escalating': 0,
  'adherence.rapid_guessing': 0,
} as const;

export type SignalType = keyof typeof SIGNAL_WEIGHTS;

export interface ProfileSignal {
  profile_id: string; // This is user_id - will be looked up to get learning_profiles.id
  source_feature: string;
  source_session_id?: string;
  signal_type: SignalType;
  signal_data: Record<string, unknown>;
  signal_weight?: number;
}

/**
 * Emit a learning signal to the profile_signals table
 * Handles profile_id lookup automatically
 */
export async function emitSignal(signal: ProfileSignal): Promise<string | null> {
  try {
    // First get or create the learning profile
    const { data: profile } = await supabase
      .from('learning_profiles')
      .select('id')
      .eq('user_id', signal.profile_id)
      .maybeSingle();

    let profileId = profile?.id;

    if (!profileId) {
      // Create profile if doesn't exist
      // Use 'any' cast as learning_profiles may have additional required fields
      const { data: newProfile, error: createError } = await (supabase
        .from('learning_profiles') as unknown as { insert: (data: unknown) => { select: (cols: string) => { single: () => Promise<{ data: { id: string } | null; error: unknown }> } } })
        .insert({ user_id: signal.profile_id })
        .select('id')
        .single();

      if (createError) {
        console.error('[SIGNAL] Failed to create profile:', String(createError));
        return null;
      }
      profileId = newProfile?.id;
    }

    if (!profileId) {
      console.error('[SIGNAL] Could not get profile_id');
      return null;
    }

    // Insert signal - use type assertion to handle schema differences
    // Note: signal_weight is stored in signal_data for processing, not as a separate column
    const signalWeight = signal.signal_weight ?? SIGNAL_WEIGHTS[signal.signal_type] ?? 0.5;
    const signalRecord = {
      profile_id: profileId,
      source_feature: signal.source_feature,
      source_session_id: signal.source_session_id || null,
      signal_type: signal.signal_type,
      signal_data: { ...signal.signal_data, _weight: signalWeight },
      processed: false,
    };

    const { data, error } = await (supabase
      .from('profile_signals') as unknown as { insert: (data: unknown) => { select: (cols: string) => { single: () => Promise<{ data: { id: string } | null; error: unknown }> } } })
      .insert(signalRecord)
      .select('id')
      .single();

    if (error) {
      console.error('[SIGNAL] Insert failed:', String(error));
      return null;
    }

    console.log(`[SIGNAL] Emitted ${signal.signal_type}`);
    return data?.id ?? null;
  } catch (err) {
    console.error('[SIGNAL] Error emitting signal:', err);
    return null;
  }
}

/**
 * Emit multiple signals in a batch
 * Optimized: Single profile lookup + batch insert (2 queries instead of 2N)
 */
export async function emitSignalBatch(signals: ProfileSignal[]): Promise<number> {
  if (signals.length === 0) return 0;

  try {
    // All signals should be for the same user, so lookup profile once
    const userId = signals[0].profile_id;

    // Get or create the learning profile (single lookup)
    const { data: profile } = await supabase
      .from('learning_profiles')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle();

    let profileId = profile?.id;

    if (!profileId) {
      // Create profile if doesn't exist
      const { data: newProfile, error: createError } = await (supabase
        .from('learning_profiles') as unknown as { insert: (data: unknown) => { select: (cols: string) => { single: () => Promise<{ data: { id: string } | null; error: unknown }> } } })
        .insert({ user_id: userId })
        .select('id')
        .single();

      if (createError) {
        console.error('[SIGNAL] Failed to create profile for batch:', String(createError));
        return 0;
      }
      profileId = newProfile?.id;
    }

    if (!profileId) {
      console.error('[SIGNAL] Could not get profile_id for batch');
      return 0;
    }

    // Build all signal records
    const signalRecords = signals.map(signal => ({
      profile_id: profileId,
      source_feature: signal.source_feature,
      source_session_id: signal.source_session_id || null,
      signal_type: signal.signal_type,
      signal_data: signal.signal_data,
      signal_weight: signal.signal_weight ?? SIGNAL_WEIGHTS[signal.signal_type] ?? 0.5,
      processed: false,
    }));

    // Batch insert all signals (single insert)
    const { data, error } = await (supabase
      .from('profile_signals') as unknown as { insert: (data: unknown[]) => { select: (cols: string) => Promise<{ data: { id: string }[] | null; error: unknown }> } })
      .insert(signalRecords)
      .select('id');

    if (error) {
      console.error('[SIGNAL] Batch insert failed:', String(error));
      return 0;
    }

    const count = data?.length ?? 0;
    console.log(`[SIGNAL] Batch emitted ${count}/${signals.length} signals`);
    return count;
  } catch (err) {
    console.error('[SIGNAL] Error in batch emit:', err);
    return 0;
  }
}

// ============================================================================
// FLASHCARD SIGNAL HELPERS
// ============================================================================

export function createFlashcardAnsweredSignal(
  profileId: string,
  cardData: {
    card_id: string;
    card_type?: string; // 'standard', 'multiple_choice', 'true_false', 'issue_spotting'
    subject?: string;
    topic?: string;
    is_correct: boolean;
    time_to_answer_ms?: number;
    // SRS enrichment fields (optional for backward compat)
    srs_rating?: string; // 'wrong', 'hard', 'got_it'
    stability_before?: number;
    stability_after?: number;
    difficulty_before?: number;
    difficulty_after?: number;
    interval_before_days?: number;
    interval_after_days?: number;
    elapsed_days?: number;
    retrievability_at_review?: number;
    review_count?: number;
    lapse_count?: number;
    is_lapse?: boolean;
    state_before?: number;
    state_after?: number;
    /**
     * Re-homed NextGen item axes. Build with `rehomedAxesFromCard(card)` —
     * it returns undefined for every ordinary card, so passing it
     * unconditionally at every call site is safe and is the point.
     */
    rehomed?: RehomedCardAxes;
  }
): ProfileSignal {
  const { rehomed, ...rest } = cardData;
  const axes = resolveFlashcardSignalAxes({ cardType: cardData.card_type, rehomed });
  return {
    profile_id: profileId,
    source_feature: 'flashcards',
    signal_type: 'flashcards.card_answered',
    signal_data: {
      ...rest,
      // Normalize subject to canonical slug for Intelligence system
      subject: cardData.subject ? normalizeSubject(cardData.subject) ?? cardData.subject : undefined,
      // Axis tags: exam_format, skill_domain (optional)
      ...axes,
    },
  };
}

export function createFlashcardMasteredSignal(
  profileId: string,
  cardData: {
    card_id: string;
    card_type?: string; // 'standard', 'multiple_choice', 'true_false', 'issue_spotting'
    subject?: string;
    topic?: string;
    consecutive_correct?: number;
    total_attempts?: number;
    // SRS enrichment fields (auto-emitted when interval crosses 21d)
    interval_days?: number;
    stability?: number;
    review_count?: number;
    lapse_count?: number;
    /**
     * Re-homed NextGen item axes. Build with `rehomedAxesFromCard(card)` —
     * it returns undefined for every ordinary card, so passing it
     * unconditionally at every call site is safe and is the point.
     */
    rehomed?: RehomedCardAxes;
  }
): ProfileSignal {
  const { rehomed, ...rest } = cardData;
  const axes = resolveFlashcardSignalAxes({ cardType: cardData.card_type, rehomed });
  return {
    profile_id: profileId,
    source_feature: 'flashcards',
    signal_type: 'flashcards.card_mastered',
    signal_data: {
      ...rest,
      // Normalize subject to canonical slug for Intelligence system
      subject: cardData.subject ? normalizeSubject(cardData.subject) ?? cardData.subject : undefined,
      ...axes,
    },
  };
}

export function createFlashcardSessionSignal(
  profileId: string,
  sessionData: {
    session_id?: string;
    cards_reviewed: number;
    correct_count: number;
    accuracy: number;
    subjects_covered: string[];
    time_spent_seconds?: number;
    // SRS enrichment fields
    wrong_count?: number;
    hard_count?: number;
    got_it_count?: number;
    cards_lapsed?: number;
    new_cards_introduced?: number;
    avg_retrievability_before?: number;
    avg_retrievability_after?: number;
  }
): ProfileSignal {
  // Session-level: no per-card card_type, so the IS branch never applies here.
  const axes = resolveFlashcardSignalAxes({});
  return {
    profile_id: profileId,
    source_feature: 'flashcards',
    source_session_id: sessionData.session_id,
    signal_type: 'flashcards.session_completed',
    signal_data: {
      ...sessionData,
      // Normalize all subjects to canonical slugs for Intelligence system
      subjects_covered: sessionData.subjects_covered.map(s => normalizeSubject(s) ?? s),
      ...axes,
    },
  };
}

/**
 * Create a signal when user pins a flashcard for focus.
 * Pin indicates a user-declared area of weakness/challenge.
 * This is a high-weight signal (0.8) because it's explicit user input.
 *
 * Pin is additive to mastery - a card can be both Pinned AND Mastered.
 * The signal feeds into JDS Intelligence to generate personalized
 * practice questions targeting the user's weak areas.
 */
export function createFlashcardPinnedSignal(
  profileId: string,
  cardData: {
    card_id: string;
    card_type?: string; // 'standard', 'multiple_choice', 'true_false', 'issue_spotting'
    subject?: string;
    topic?: string;
    is_pinned: boolean;  // true = pinned, false = unpinned
    /**
     * Re-homed NextGen item axes. Build with `rehomedAxesFromCard(card)` —
     * it returns undefined for every ordinary card, so passing it
     * unconditionally at every call site is safe and is the point.
     */
    rehomed?: RehomedCardAxes;
  }
): ProfileSignal {
  const { rehomed, ...rest } = cardData;
  const axes = resolveFlashcardSignalAxes({ cardType: cardData.card_type, rehomed });
  return {
    profile_id: profileId,
    source_feature: 'flashcards',
    signal_type: 'flashcards.card_pinned',
    signal_data: {
      ...rest,
      // Normalize subject to canonical slug for Intelligence system
      subject: cardData.subject ? normalizeSubject(cardData.subject) ?? cardData.subject : undefined,
      ...axes,
    },
  };
}

/**
 * Create a signal when a previously learned card is forgotten (lapse).
 * A lapse = rating Wrong on a card with scheduled_days >= 7.
 * High-weight signal (0.8) — forgetting something you knew is strong
 * evidence of a knowledge gap.
 */
export function createFlashcardLapsedSignal(
  profileId: string,
  cardData: {
    card_id: string;
    card_type?: string; // 'standard', 'multiple_choice', 'true_false', 'issue_spotting'
    subject?: string;
    topic?: string;
    previous_interval_days: number;
    previous_stability: number;
    lapse_count: number;
    days_overdue?: number;
    retrievability_at_review?: number;
    /**
     * Re-homed NextGen item axes. Build with `rehomedAxesFromCard(card)` —
     * it returns undefined for every ordinary card, so passing it
     * unconditionally at every call site is safe and is the point.
     */
    rehomed?: RehomedCardAxes;
  }
): ProfileSignal {
  const { rehomed, ...rest } = cardData;
  const axes = resolveFlashcardSignalAxes({ cardType: cardData.card_type, rehomed });
  return {
    profile_id: profileId,
    source_feature: 'flashcards',
    signal_type: 'flashcards.card_lapsed',
    signal_data: {
      ...rest,
      subject: cardData.subject ? normalizeSubject(cardData.subject) ?? cardData.subject : undefined,
      // Axis tags: exam_format, skill_domain (optional)
      ...axes,
    },
  };
}

/**
 * Create a signal when user pins an Issue Spotter issue for focus.
 * Pin indicates a user-declared area of weakness/challenge.
 * This is a high-weight signal (0.8) because it's explicit user input.
 *
 * Pin is additive - an issue can be both Spotted AND Pinned.
 * The signal feeds into JDS Intelligence to generate personalized
 * practice scenarios targeting the user's weak areas.
 */
export function createIssuePinnedSignal(
  profileId: string,
  issueData: {
    issue_id: string;
    subject_area: string;
    issue_category: string;
    issue_statement: string;
    is_pinned: boolean;  // true = pinned, false = unpinned
    session_id?: string;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'issue_spotter',
    source_session_id: issueData.session_id,
    signal_type: 'issue_spotter.issue_pinned',
    signal_data: {
      ...issueData,
      // Normalize subject_area to canonical slug for Intelligence system
      subject_area: normalizeSubject(issueData.subject_area) ?? issueData.subject_area,
      exam_format: 'iqs',  // 3-axis: issue spotter → IQS format
      skill_domain: 'issue_spotting_analysis',
    },
  };
}

// ============================================================================
// COURSE LESSON SIGNAL HELPERS
// ============================================================================

/**
 * Create a signal when a user completes a course lesson.
 * Low-weight signal (0.15) — lesson completion is passive exposure,
 * not active practice. The completed_at timestamp in lesson_progress
 * enables knowledge decay weighting.
 *
 * Applies to both JDS standard courses and NextGen courses.
 */
export function createLessonCompletedSignal(
  profileId: string,
  lessonData: {
    lesson_id: string;
    course_id: string;
    course_type: string;        // 'nextgen' | 'standard'
    subject?: string;            // canonical slug from course_subjects resolution
    lesson_title?: string;
    module_id?: string;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'course',
    signal_type: 'course.lesson_completed',
    signal_data: {
      ...lessonData,
      subject: lessonData.subject ? normalizeSubject(lessonData.subject) ?? lessonData.subject : undefined,
    },
  };
}

/**
 * Self-reported miss reason values for MCQ questions.
 * Used in the "Why did you miss this?" UI after incorrect answers.
 */
export type MissReason =
  | 'didnt_know_rule'
  | 'knew_rule_applied_wrong'
  | 'misread_facts'
  | 'confused_with_similar_rule'
  | 'guessed';

/**
 * Create a signal when a user self-reports why they missed an MCQ question.
 * High-weight signal (0.8) — explicit user input about root cause of error.
 * Emitted separately from the question_answered signal (Option A pattern).
 */
export function createMissReasonSignal(
  profileId: string,
  data: {
    question_id: string;
    self_reported_reason: MissReason;
    subject?: string;
    practice_mode: string;
    /**
     * Exam format the question was practiced under. Defaults to 'mcq' for
     * backward compatibility with MCQ practice callers that don't thread it
     * through yet. IQS and PT practice pages MUST pass this explicitly so
     * the signal attributes correctly to their format's accuracy pipeline.
     */
    exam_format?: 'mcq' | 'mcq_b' | 'iqs' | 'pt' | 'lrpt';
    session_context?: { session_mode: 'practice' | 'simulation' };
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'nextgen',
    signal_type: 'nextgen.miss_reason_reported',
    signal_data: {
      question_id: data.question_id,
      self_reported_reason: data.self_reported_reason,
      subject: data.subject ? normalizeSubject(data.subject) ?? data.subject : null,
      exam_format: data.exam_format ?? 'mcq',
      practice_mode: data.practice_mode,
      session_context: data.session_context ?? null,
    },
  };
}

export function createNextGenMasteredSignal(
  profileId: string,
  data: {
    question_id: string;
    subject?: string;
    skill_domain?: string;
    exam_format?: string;
    difficulty?: number;
    /** Primary NCBE task codes (e.g. ['T6']) — used by signal_processor.py for subcategory resolution */
    ncbe_tasks?: string[];
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'nextgen',
    signal_type: 'nextgen.question_mastered',
    signal_data: {
      question_id: data.question_id,
      subject: data.subject ? normalizeSubject(data.subject) ?? data.subject : null,
      skill_domain: data.skill_domain ?? null,
      exam_format: data.exam_format ?? null,
      difficulty: data.difficulty ?? null,
      ncbe_tasks: data.ncbe_tasks ?? null,
    },
  };
}

export function createNextGenLapsedSignal(
  profileId: string,
  data: {
    question_id: string;
    subject?: string;
    skill_domain?: string;
    exam_format?: string;
    difficulty?: number;
    lapse_count?: number;
    /** Primary NCBE task codes (e.g. ['T6']) — used by signal_processor.py for subcategory resolution */
    ncbe_tasks?: string[];
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'nextgen',
    signal_type: 'nextgen.question_lapsed',
    signal_data: {
      question_id: data.question_id,
      subject: data.subject ? normalizeSubject(data.subject) ?? data.subject : null,
      skill_domain: data.skill_domain ?? null,
      exam_format: data.exam_format ?? null,
      difficulty: data.difficulty ?? null,
      lapse_count: data.lapse_count ?? null,
      ncbe_tasks: data.ncbe_tasks ?? null,
    },
  };
}

/**
 * Create a signal when the user sees the Overall Readiness tier transition
 * toast for a given tier. Frontend-only marker — zero weight, not a learning
 * event. Used to suppress re-showing the celebration across sessions/devices.
 *
 * Stored in profile_signals so suppression is cross-device (localStorage was
 * intentionally rejected for this use case).
 *
 * Tiers are one-way: we never delete a seen signal. If a user's tier drops
 * (e.g. Strong → Building because of new wrong answers) and then climbs back,
 * the toast is suppressed because the original seen signal still exists. This
 * prevents oscillation at tier boundaries.
 */
export function createTierTransitionSeenSignal(
  profileId: string,
  data: {
    tier: 'building' | 'strong' | 'mastered';
    readiness_score: number;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'nextgen',
    signal_type: 'nextgen.tier_transition_seen',
    signal_data: {
      tier: data.tier,
      readiness_score: data.readiness_score,
      seen_at: new Date().toISOString(),
    },
  };
}

// ============================================================================
// WORKBENCH SIGNAL HELPERS
// ============================================================================

/**
 * Create a signal when a NextGen workbench task is completed.
 * Moderate-weight signal (0.70) — active practice with timing and engagement data.
 * Feeds subject, skill_domain, and exam_format into the 3-axis belief model.
 */
export function createWorkbenchTaskCompletedSignal(params: {
  profileId: string;
  taskId: string;
  taskType: string;
  examFormat: string;
  skillDomain: string;
  subjects: string[];
  mode: string;
  timeSpentSeconds: number;
  sourcesViewed: number;
  highlightsCount: number;
  itemsCompleted?: number;
  itemsCorrect?: number;
}): ProfileSignal {
  return {
    profile_id: params.profileId,
    source_feature: 'workbench',
    signal_type: 'workbench.task_completed',
    signal_data: {
      task_id: params.taskId,
      task_type: params.taskType,
      exam_format: params.examFormat,
      skill_domain: params.skillDomain,
      subjects: params.subjects.map(s => normalizeSubject(s) ?? s),
      mode: params.mode,
      time_spent_seconds: params.timeSpentSeconds,
      sources_viewed: params.sourcesViewed,
      highlights_count: params.highlightsCount,
      items_completed: params.itemsCompleted ?? null,
      items_correct: params.itemsCorrect ?? null,
    },
  };
}

/**
 * Create a signal when a NextGen workbench task is graded by AI.
 * High-weight signal (0.85) — structured rubric scoring with per-skill breakdown.
 * Feeds subject, skill_domain, and exam_format into the 3-axis belief model.
 */
export function createWorkbenchTaskGradedSignal(params: {
  profileId: string;
  taskId: string;
  responseId: string;
  taskType: string;
  examFormat: string;
  skillDomain: string;
  subjects: string[];
  overallScore: number;
  maxScore: number;
  rubricScores: Array<{ criterion: string; skill: string; score: number; max: number }>;
  gradingModel: string;
}): ProfileSignal {
  return {
    profile_id: params.profileId,
    source_feature: 'workbench',
    signal_type: 'workbench.task_graded',
    signal_data: {
      task_id: params.taskId,
      response_id: params.responseId,
      task_type: params.taskType,
      exam_format: params.examFormat,
      skill_domain: params.skillDomain,
      subjects: params.subjects.map(s => normalizeSubject(s) ?? s),
      overall_score: params.overallScore,
      max_score: params.maxScore,
      rubric_scores: params.rubricScores,
      grading_model: params.gradingModel,
    },
  };
}

// ============================================================================
// STUDY PLAN / ACTIVITY SIGNAL HELPERS
// ============================================================================

/**
 * Create a signal when a study plan task is completed.
 * This is a high-weight signal (0.75) indicating explicit learning progress.
 */
export function createTaskCompletedSignal(
  profileId: string,
  taskData: {
    task_id: string;
    task_type: string;
    subject?: string;
    topic?: string;
    estimated_minutes?: number;
    actual_minutes?: number;
    study_plan_id?: string;
    completion_notes?: string;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'study_plan',
    signal_type: 'study_plan.task_completed',
    signal_data: {
      ...taskData,
      // Normalize subject to canonical slug for Intelligence system
      subject: taskData.subject ? normalizeSubject(taskData.subject) ?? taskData.subject : undefined,
    },
  };
}

/**
 * Create a signal when a diagnostic assessment is completed.
 * This is the highest-weight study signal (0.85) - direct skill assessment.
 */
export function createDiagnosticCompletedSignal(
  profileId: string,
  diagnosticData: {
    task_id: string;
    subject: string;
    score_percentage?: number;
    questions_answered?: number;
    time_spent_seconds?: number;
    study_plan_id?: string;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'study_plan',
    signal_type: 'study_plan.diagnostic_completed',
    signal_data: {
      ...diagnosticData,
      // Normalize subject to canonical slug for Intelligence system
      subject: normalizeSubject(diagnosticData.subject) ?? diagnosticData.subject,
    },
  };
}

/**
 * Create a signal when a task is skipped/dismissed.
 * Lower weight (0.5) but still useful for understanding user preferences.
 */
export function createActivitySkippedSignal(
  profileId: string,
  taskData: {
    task_id: string;
    task_type: string;
    subject?: string;
    topic?: string;
    reason?: string;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'study_plan',
    signal_type: 'study_plan.activity_skipped',
    signal_data: {
      ...taskData,
      // Normalize subject to canonical slug for Intelligence system
      subject: taskData.subject ? normalizeSubject(taskData.subject) ?? taskData.subject : undefined,
    },
  };
}

// ============================================================================
// SYLLABUS SIGNAL HELPERS
// ============================================================================

/**
 * Create a signal when tasks are generated from a syllabus.
 * This informs Intelligence about the user's course schedule and subjects.
 */
export function createSyllabusTasksGeneratedSignal(
  profileId: string,
  syllabusData: {
    syllabus_id: string;
    course_name: string;
    exam_tasks_created: number;
    assignment_tasks_created: number;
    subjects_covered: string[];
    upcoming_exam_dates?: string[];
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'syllabus',
    signal_type: 'syllabus.tasks_generated',
    signal_data: {
      ...syllabusData,
      // Normalize all subjects to canonical slugs for Intelligence system
      subjects_covered: syllabusData.subjects_covered.map(s => normalizeSubject(s) ?? s),
    },
  };
}

// ============================================================================
// ONBOARDING SIGNAL HELPERS
// ============================================================================

/**
 * Create a signal when user declares their status during onboarding.
 * Cold start signal (0.4 weight) — seeds the learning profile.
 */
export function createStatusDeclaredSignal(
  profileId: string,
  status: string
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'onboarding',
    signal_type: 'onboarding.status_declared',
    signal_data: {
      current_status: status,
    },
  };
}

/**
 * Create a signal when user declares their goal during onboarding.
 * Cold start signal (0.4 weight) — seeds the learning profile.
 */
export function createGoalDeclaredSignal(
  profileId: string,
  goal: string,
  status: string
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'onboarding',
    signal_type: 'onboarding.goal_declared',
    signal_data: {
      goal_mode: goal,
      current_status: status,
    },
  };
}

/**
 * Create a signal when onboarding is completed (all profile data declared).
 * Cold start signal (0.4 weight) — full profile snapshot for Intelligence bootstrapping.
 */
export function createProfileDeclaredSignal(
  profileId: string,
  profileData: {
    current_status: string;
    goal_mode: string;
    courses: string[];
    // Declared bar jurisdiction (journey context for Study Plan). Optional —
    // only law-school / bar-prep users select a target_bar_state during onboarding.
    target_bar_state?: string | null;
  }
): ProfileSignal {
  return {
    profile_id: profileId,
    source_feature: 'onboarding',
    signal_type: 'onboarding.profile_declared',
    signal_data: {
      ...profileData,
      courses: profileData.courses.map(s => normalizeSubject(s) ?? s),
    },
  };
}
